/**
 * Rainy Window - static camera, rain running down glass over city bokeh.
 * All droplets complete an integer number of falls per loop.
 */

import { createRng, drawGrain, drawVignette, glow, reactive, withAlpha } from './shared.js';

const WORLD = (() => {
  const rng = createRng(0x2a1d0f);

  const bokeh = Array.from({ length: 26 }, () => ({
    nx: rng(),
    ny: rng() * 0.85,
    radius: 0.02 + rng() * 0.075,
    cycles: 1 + Math.floor(rng() * 2),
    offset: rng(),
    warm: rng(),
    brightness: 0.25 + rng() * 0.5,
  }));

  const droplets = Array.from({ length: 44 }, () => ({
    nx: rng(),
    startY: -0.1 + rng() * 1.1,
    cycles: 1 + Math.floor(rng() * 3),
    offset: rng(),
    length: 0.02 + rng() * 0.11,
    width: 0.0018 + rng() * 0.0055,
    wobble: rng() * Math.PI * 2,
    alpha: 0.12 + rng() * 0.3,
  }));

  const mist = Array.from({ length: 12 }, () => ({
    nx: rng(),
    ny: rng(),
    radius: 0.1 + rng() * 0.2,
    cycles: 1 + Math.floor(rng() * 2),
    offset: rng(),
  }));

  return { bokeh, droplets, mist };
})();

export default {
  id: 'rain-window',
  label: 'Rainy Window',
  description: 'Procedural rain on glass over out-of-focus city lights.',
  loopable: true,

  draw(ctx, { t, loopSeconds, w, h, audio, strength }) {
    const phase = (t % loopSeconds) / loopSeconds;
    const level = reactive(audio.level, strength);
    const treble = reactive(audio.treble, strength);

    // --- night behind the glass
    const bg = ctx.createLinearGradient(0, 0, w * 0.3, h);
    bg.addColorStop(0, '#05070C');
    bg.addColorStop(0.5, '#0A1018');
    bg.addColorStop(1, '#060809');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);

    // --- mist patches
    for (const m of WORLD.mist) {
      const drift = 0.5 + 0.5 * Math.sin((phase + m.offset) * Math.PI * 2 * m.cycles);
      glow(ctx, m.nx * w, m.ny * h, m.radius * w, '#1E2836', 0.25 + drift * 0.15);
    }

    // --- bokeh city lights (out of focus)
    for (const b of WORLD.bokeh) {
      const breathe = 0.6 + 0.4 * Math.sin((phase + b.offset) * Math.PI * 2 * b.cycles);
      const color = b.warm > 0.66 ? '#FFB36B' : b.warm > 0.33 ? '#FFE0B0' : '#9FC4FF';
      const radius = b.radius * w * (1 + level * 0.06);
      glow(ctx, b.nx * w, b.ny * h, radius, color, b.brightness * breathe * (0.7 + level * 0.5));
      // Hard-ish core so the bokeh reads as a light, not just fog.
      ctx.fillStyle = withAlpha(color, b.brightness * breathe * 0.5);
      ctx.beginPath();
      ctx.arc(b.nx * w, b.ny * h, radius * 0.28, 0, Math.PI * 2);
      ctx.fill();
    }

    // --- glass plane tint
    const glass = ctx.createLinearGradient(0, 0, w, h);
    glass.addColorStop(0, 'rgba(120,150,180,0.06)');
    glass.addColorStop(0.5, 'rgba(60,80,100,0.03)');
    glass.addColorStop(1, 'rgba(140,170,200,0.05)');
    ctx.fillStyle = glass;
    ctx.fillRect(0, 0, w, h);

    // --- droplet trails
    ctx.lineCap = 'round';
    for (const d of WORLD.droplets) {
      const fall = (d.offset + phase * d.cycles) % 1;
      const wobble = Math.sin((phase + d.offset) * Math.PI * 2) * 0.004;
      const x = (d.nx + wobble) * w;
      const y = (d.startY + fall) * h;
      const len = d.length * h;
      const width = Math.max(1, d.width * w);

      const trail = ctx.createLinearGradient(x, y - len, x, y);
      const bright = 0.55 + treble * 0.45;
      trail.addColorStop(0, withAlpha('#CFE4FF', 0));
      trail.addColorStop(0.7, withAlpha('#CFE4FF', d.alpha * bright));
      trail.addColorStop(1, withAlpha('#EAF4FF', Math.min(1, d.alpha * 1.7 * bright)));
      ctx.strokeStyle = trail;
      ctx.lineWidth = width;
      ctx.beginPath();
      ctx.moveTo(x, y - len);
      ctx.lineTo(x, y);
      ctx.stroke();

      // the bead itself
      ctx.fillStyle = withAlpha('#EAF4FF', Math.min(0.85, d.alpha * 2.1 * bright));
      ctx.beginPath();
      ctx.arc(x, y, width * 1.15, 0, Math.PI * 2);
      ctx.fill();
    }

    // --- condensation at the edges
    const edge = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.28, w / 2, h / 2, Math.max(w, h) * 0.72);
    edge.addColorStop(0, 'rgba(0,0,0,0)');
    edge.addColorStop(1, 'rgba(150,175,200,0.10)');
    ctx.fillStyle = edge;
    ctx.fillRect(0, 0, w, h);

    drawVignette(ctx, w, h, 0.6);
    drawGrain(ctx, w, h, { t, alpha: 0.028 });
  },
};
